import type { Access, Branch } from '@/api/types'
import type { Caller } from './abilities'
import { isArchived } from './branches'
import { t } from '@/i18n'
import { atLeast } from './permissions'
import { canonicalZone, DEFAULT_ZONE } from './timezones'

/// «Вхід» — the pure part of the section. SPEC: specs/access.md. No clock,
/// no network: the screens pass `now` and the club's time zone in, so every
/// sentence and every bar is a table test.

// ---- who sees what (specs/roles.md §3, modules `checkin` and `settings`) ----

/// «Вхід → Шафки» (specs/access.md §10b) follows `visits` to read and
/// `admit` to rent out, renew and end — the server's `checkin` levels.
export type EntryAbilities = Readonly<{
  /// The section exists: someone who sees visits, or sets the lockers up.
  see: boolean
  /// Visits, occupancy, statistics, the log — `checkin ≥ view`.
  visits: boolean
  /// «Рецепція», «Вийшов», keys — `checkin ≥ edit`.
  admit: boolean
  /// «Пустити все одно» past anti-passback — `checkin = manage` or the owner.
  override: boolean
  /// Locker mode, key board, auto-close — `settings ≥ edit`.
  setUp: boolean
  /// Readers, terminals, turnstiles — `settings = manage`.
  devices: boolean
}>

export const entryAbilities = (caller: Caller): EntryAbilities => {
  const p = caller.profile.permissions
  const visits = caller.god || atLeast(p.checkin, 'view')
  const setUp = caller.god || atLeast(p.settings, 'edit')
  return {
    see: visits || setUp,
    visits,
    admit: caller.god || atLeast(p.checkin, 'edit'),
    override: caller.ownerOrGod || p.checkin === 'manage',
    setUp,
    devices: caller.god || p.settings === 'manage',
  }
}

/// The first tab this person may open — «Рецепція» for the desk.
export const firstEntryTab = (can: EntryAbilities): 'desk' | 'inside' | 'settings' | null =>
  can.admit ? 'desk' : can.visits ? 'inside' : can.setUp ? 'settings' : null

// ---- the club the screen is about ----

/// The clubs the caller can stand at: open ones within their reach.
export const entryBranches = (access: Access, branches: readonly Branch[]): readonly Branch[] => {
  const mine = access.god ? null : (access.branchIds ?? null)
  return branches.filter((b) => !isArchived(b) && (mine === null || mine.includes(b.id)))
}

/// The remembered club when it is still on offer, else the first one.
export const pickBranch = (options: readonly Branch[], remembered: string | null): Branch | null =>
  options.find((b) => b.id === remembered) ?? options[0] ?? null

// ---- what the scanner typed ----

export type ScanKind = 'qr' | 'card' | 'search'

/// A USB reader and a camera both «type» into the one field. Our QR always
/// starts `ea1.`; a run of digits is a card (or a phone — the card lookup
/// falls back to the search); anything else is a name to search for.
export const scanKind = (text: string): ScanKind => {
  const value = text.trim()
  if (value.startsWith('ea1.')) return 'qr'
  if (/^\d[\d\s-]{3,}$/.test(value)) return 'card'
  return 'search'
}

// ---- words ----

/// One sentence for every refusal the door can give — access's own and the
/// pass rules' (i18n/uk-access.ts `refusals`). The server's own `message`
/// wins when it sent one.
const withStop = (text: string): string => (/[.!?…]$/.test(text) ? text : `${text}.`)

export const refusalText = (reason: string | null, message?: string | null): string =>
  message?.trim() ? withStop(message.trim()) : (reason ? t.access.refusals[reason] : undefined) ?? t.access.refusedGeneric

/// «45 хв», «1 год 20 хв», «3 год» — how long somebody has been inside.
export const durationText = (minutes: number): string => {
  const d = t.access.duration
  const total = Math.max(0, Math.round(minutes))
  if (total < 1) return d.justNow
  const hours = Math.floor(total / 60)
  const rest = total % 60
  if (hours === 0) return d.minutes(rest)
  return rest === 0 ? d.hours(hours) : d.hoursMinutes(hours, rest)
}

export const minutesBetween = (fromIso: string, now: Date): number =>
  (now.getTime() - new Date(fromIso).getTime()) / 60_000

/// «18:02» in the club's own zone — a receptionist reads the wall clock.
export const clockTime = (iso: string, timeZone: string): string =>
  new Intl.DateTimeFormat('uk-UA', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
    new Date(iso),
  )

/// The club's calendar day of an instant, as YYYY-MM-DD.
export const localDate = (instant: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(
    instant,
  )
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '01'
  return `${get('year')}-${get('month')}-${get('day')}`
}

const addDays = (isoDate: string, days: number): string => {
  const [y = 1970, m = 1, d = 1] = isoDate.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

/// «Сьогодні», «7 днів», «30 днів» — always ending today, in the club's zone.
export const STAT_PRESETS = ['today', 'week', 'month'] as const
export type StatPreset = (typeof STAT_PRESETS)[number]

export const presetRange = (preset: StatPreset, today: string): Readonly<{ from: string; to: string }> => ({
  from: preset === 'today' ? today : addDays(today, preset === 'week' ? -6 : -29),
  to: today,
})

/// Every day of a range, so a day nobody came is a zero bar, not a gap.
export const daysOf = (from: string, to: string): readonly string[] => {
  const out: string[] = []
  for (let day = from; day <= to && out.length < 400; day = addDays(day, 1)) out.push(day)
  return out
}

// ---- charts ----

/// A clean top for an axis and the ticks under it: 0, a round step, … —
/// «0 / 5 / 10 / 15», never «0 / 4.3 / 8.6». At most `count + 1` ticks.
export const niceScale = (max: number, count = 4): Readonly<{ top: number; ticks: readonly number[] }> => {
  if (!(max > 0)) return { top: 1, ticks: [0, 1] }
  const raw = max / count
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step = ([1, 2, 2.5, 5, 10].map((f) => f * magnitude).find((s) => s >= raw) ?? 10 * magnitude)
  const top = Math.ceil(max / step) * step
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => Math.round(i * step * 100) / 100)
  return { top, ticks }
}

/// Bar heights in px for values against a top; a non-zero value never
/// vanishes into a 0 px bar (2 px at least, so «there was somebody» shows).
export const barHeights = (values: readonly number[], top: number, height: number): readonly number[] =>
  values.map((v) => (v <= 0 || top <= 0 ? 0 : Math.max(2, Math.round((v / top) * height))))

/// The busiest hour of the curve, or null when nobody came.
export const busiestHour = (hourly: readonly number[]): number | null => {
  const max = Math.max(0, ...hourly)
  return max > 0 ? hourly.indexOf(max) : null
}

/// «2,5» — averages with one decimal, Ukrainian comma, no trailing «,0».
export const decimal = (value: number): string =>
  new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 1 }).format(value)

/// Entries of a `{ key: count }` map, the biggest first.
export const ranked = (counts: Readonly<Record<string, number>>): ReadonlyArray<readonly [string, number]> =>
  Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))

// ---- lockers (§10) ----

export const LOCKER_MODES = ['keys', 'self', 'none'] as const
export type LockerMode = (typeof LOCKER_MODES)[number]

export type LockerState = 'free' | 'issued' | 'lost' | 'out_of_service'

/// The key the desk is offered for `clientId` (the board comes sorted by the
/// server). A renter gets the locker they rent today when its key can go to
/// them — on its hook, or still with them from an earlier visit; anybody else
/// the lowest free hook that nobody rents today (specs/access.md §10b, LR2) —
/// the server's own rule (services/access entry.ts `suggestLocker`).
export const suggestLocker = (
  lockers: readonly Readonly<{
    number: string
    state: string
    clientId?: string | null
    rental?: Readonly<{ clientId: string }> | null
  }>[],
  clientId: string | null = null,
): string | null => {
  const own =
    clientId === null
      ? undefined
      : lockers.find(
          (l) => l.rental?.clientId === clientId && (l.state === 'free' || (l.state === 'issued' && l.clientId === clientId)),
        )
  return own?.number ?? lockers.find((l) => l.state === 'free' && !l.rental)?.number ?? null
}

/// A key out with the person who rents its locker is theirs for the period,
/// not a debt: «Ключі не повернуто» leaves it out (§10b «The renter's key»).
export const heldByRenter = (locker: Readonly<{ clientId: string | null; rental?: Readonly<{ clientId: string }> | null }>): boolean =>
  Boolean(locker.rental && locker.clientId && locker.rental.clientId === locker.clientId)

/// The board back as a manager would type it: «1-80, Ж1-Ж10, 81А». Runs of
/// consecutive numbers with the same prefix fold into a range.
export const boardText = (numbers: readonly string[]): string => {
  const parts: string[] = []
  let run: { prefix: string; from: number; to: number } | null = null
  const flush = () => {
    if (!run) return
    parts.push(run.from === run.to ? `${run.prefix}${run.from}` : `${run.prefix}${run.from}-${run.prefix}${run.to}`)
    run = null
  }
  for (const number of numbers) {
    const match = /^([^\d]*)(\d+)$/.exec(number)
    if (!match || match[2]?.startsWith('0')) {
      flush()
      parts.push(number)
      continue
    }
    const prefix = match[1] ?? ''
    const n = Number(match[2])
    if (run && run.prefix === prefix && n === run.to + 1) run.to = n
    else {
      flush()
      run = { prefix, from: n, to: n }
    }
  }
  flush()
  return parts.join(', ')
}

/// The same parse the server applies (services/access entry.ts
/// parseLockerList) — so the form can count the keys and catch a typo before
/// saving. Null when the text does not parse.
export const parseBoard = (text: string): readonly string[] | null => {
  const out: string[] = []
  for (const raw of text.split(/[,;\n]/)) {
    const part = raw.trim()
    if (!part) continue
    const range = /^([^\d\s-]*)(\d+)\s*-\s*\1?(\d+)$/.exec(part)
    if (range) {
      const [, prefix = '', fromText = '', toText = ''] = range
      const from = Number(fromText)
      const to = Number(toText)
      if (to < from || to - from > 1000) return null
      for (let n = from; n <= to; n += 1) out.push(`${prefix}${n}`)
    } else if (/^[\p{L}\d][\p{L}\d-]{0,11}$/u.test(part)) {
      out.push(part)
    } else {
      return null
    }
    if (out.length > 1000) return null
  }
  return new Set(out).size === out.length ? out : null
}

// ---- devices (§9) ----

export const DEVICE_STALE_MINUTES = 5

export type DeviceHealth = 'never' | 'ok' | 'stale' | 'off'

export const deviceHealth = (
  device: Readonly<{ lastSeenAt: string | null; disabled: boolean }>,
  now: Date,
): DeviceHealth => {
  if (device.disabled) return 'off'
  if (!device.lastSeenAt) return 'never'
  return minutesBetween(device.lastSeenAt, now) > DEVICE_STALE_MINUTES ? 'stale' : 'ok'
}

// ---- the verdict at the desk ----

export type PassChoice = Readonly<{ id: string; ok: boolean }>

/// The pass the door would spend: the first that admits, in the door's own
/// order (the server sends them sorted). The desk may pick another.
export const defaultPass = (passes: readonly PassChoice[]): string | null => passes.find((p) => p.ok)?.id ?? null

// ---- one client's visits (§7.1) ----

/// What a row of a client's history needs to know about a visit.
export type VisitTimes = Readonly<{ enteredAt: string; leftAt: string | null; leftBy: string | null; inside: boolean }>

/// - `inside` — still in the club: minutes run on the screen only;
/// - `timed` — somebody saw them go (the exit or the desk): it has a duration;
/// - `unrecorded` — it closed itself: NO duration (V2) — «вихід не відмічено».
export type VisitPhase = 'inside' | 'timed' | 'unrecorded'

export const visitPhase = (visit: VisitTimes): VisitPhase =>
  visit.inside ? 'inside' : visit.leftAt !== null && visit.leftBy !== 'auto' ? 'timed' : 'unrecorded'

/// V2: how long the visit was — only when somebody saw it end. Null for one
/// that closed itself and for one still open.
export const visitMinutes = (visit: VisitTimes): number | null =>
  visitPhase(visit) === 'timed' && visit.leftAt ? minutesBetween(visit.enteredAt, new Date(visit.leftAt)) : null

/// «Сьогодні», «Учора», «Чт, 29 жовт.», «Пн, 29 груд. 2025» — the club's
/// calendar day of a visit, the year only when it is not this one. A row
/// starts with it, so it starts with a capital like «Сьогодні» above it.
export const visitDay = (iso: string, timeZone: string, now: Date): string => {
  const v = t.access.visits
  const day = localDate(new Date(iso), timeZone)
  const today = localDate(now, timeZone)
  if (day === today) return v.today
  if (day === addDays(today, -1)) return v.yesterday
  const text = new Intl.DateTimeFormat('uk-UA', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(day.slice(0, 4) === today.slice(0, 4) ? {} : { year: 'numeric' }),
  })
    .format(new Date(iso))
    // « 2025 р.» → « 2025»; the space keeps «вер.» and «бер.» whole.
    .replace(/\s+р\.$/, '')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/// «18:05–19:17», or «з 07:40» when the end is not known (still inside, or
/// nobody saw them go).
export const visitHours = (visit: VisitTimes, timeZone: string): string => {
  const v = t.access.visits
  const from = clockTime(visit.enteredAt, timeZone)
  return visitPhase(visit) === 'timed' && visit.leftAt ? v.span(from, clockTime(visit.leftAt, timeZone)) : v.since(from)
}

/// A total or an average of minutes: «3 год 42 хв», «0 хв»; «—» when there
/// is nothing to measure (no visit had its exit recorded).
export const minutesText = (minutes: number | null): string =>
  minutes === null ? t.access.visits.none : minutes < 1 ? t.access.duration.minutes(0) : durationText(minutes)

/// `2026-10` → «жовтень» / «у жовтні».
export const monthName = (month: string): string => t.access.visits.months[Number(month.slice(5, 7)) - 1] ?? month
export const inMonth = (month: string): string => t.access.visits.monthsIn[Number(month.slice(5, 7)) - 1] ?? month

/// `2026-10` — the club's calendar month a visit began in.
export const visitMonth = (iso: string, timeZone: string): string => localDate(new Date(iso), timeZone).slice(0, 7)

/// A month over its visits: «Жовтень», «Грудень 2025» — the year only when
/// it is not this one.
export const monthTitle = (month: string, now: Date, timeZone: string): string => {
  const name = monthName(month)
  const title = name.charAt(0).toUpperCase() + name.slice(1)
  return month.slice(0, 4) === localDate(now, timeZone).slice(0, 4) ? title : `${title} ${month.slice(0, 4)}`
}

/// The company's home zone, its first club still open — the rule
/// services/orgs answers as `homeTimeZone` (specs/tenancy.md, Branches):
/// a client of the whole network counts months on it. `branches` come in the
/// order the clubs were opened (`GET /orgs/{orgId}/branches`).
export const homeZone = (branches: readonly Pick<Branch, 'timezone' | 'status'>[]): string =>
  canonicalZone((branches.find((b) => !isArchived(b)) ?? branches[0])?.timezone ?? DEFAULT_ZONE)

/// V3: the clock a client's months are counted on — their club's, or for a
/// client of the whole network the company's home zone (the server's
/// `homeTimeZone` when it sends one).
export const clientZone = (
  clientBranchId: string | null,
  branches: readonly Pick<Branch, 'id' | 'timezone' | 'status'>[],
  homeTimeZone?: string | undefined,
): string => {
  const own = clientBranchId ? branches.find((b) => b.id === clientBranchId) : undefined
  return own ? canonicalZone(own.timezone) : homeTimeZone ? canonicalZone(homeTimeZone) : homeZone(branches)
}

// ---- refusals of the section's writes, by `data.code` ----

export const accessErrorText = (code: string | null): string | null => {
  const e = t.access.errors
  switch (code) {
    case 'locker_taken':
      return e.lockerTaken
    case 'locker_unknown':
      return e.lockerUnknown
    case 'locker_rented':
      return e.lockerRented
    case 'no_key':
      return e.noKey
    case 'not_keys_mode':
      return e.notKeysMode
    case 'visit_over':
      return e.visitOver
    case 'unknown_visit':
      return e.unknownVisit
    case 'unknown_device':
      return e.unknownDevice
    case 'invalid_lockers':
      return t.access.settings.invalidBoard
    default:
      return null
  }
}
